import type { TWidget } from 'widgets/Widget';
import { timeout } from 'widgets/toolbox/util';

import { TCheckoutMgr } from 'widgets/checkout/CheckoutMgr';
import { TBillingFormInstance } from 'widgets/checkout/BillingForm';
import { TShippingFormInstance } from 'widgets/checkout/ShippingForm';
import { TOrderModel } from 'types/models/order';

/**
 * @description Base CheckoutSummaryMixin implementation
 *
 * This class is not intended to have a separate DOM representation.
 *
 * @param CheckoutMgr Base widget for extending
 * @returns Add to Cart mixin widget
 */
function CheckoutSummaryMixinClassCreator(CheckoutMgr: TCheckoutMgr) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CheckoutSummaryMixin
     * @augments TCheckoutMgr
     * @subcategory checkout
     * @classdesc Represents checkout summary component with the next features:
     * 1. Manage Summary step state on the checkout
     * 2. Update summary on checkout steps changes
     * 3. Submit summary form and handle response
     *
     * @property {string} data-text-network-error - network error text
     */
    class CheckoutSummaryMixin extends CheckoutMgr {
        /**
         * @description Initialize widget logic
         */
        init() {
            super.init();
            this.onDestroy(timeout(() => {
                const basket = this.prefs().basket;

                this.updateSummary(basket);
            }));
            this.eventBus().on('billing.address.corrected', 'updateSummary');
            this.eventBus().on('order.update', 'updateSummary');
        }

        /**
         * @description Update summary by event from child element
         * @param widget - child widget, that fired event
         * @param order - order data
         * @listens ShippingForm#updateshippingform
         */
        updateSummaryEv(widget: TWidget, order) {
            this.updateSummary(order);
        }

        /**
         * @description Update summary block
         * @param order order data
         * @param isPreventButtonUpdating Flag to detect if need to prevent updating of the next step button data
         * @emits "order.updated"
         * @emits "checkout.step.updated"
         */
        updateSummary(order?, isPreventButtonUpdating?) {
            if (order) {
                this.order = order;

                this.updateSelectedPaymentMethods(order);
            }

            if (this.order) {
                this.order.isShipping = this.currentStep === 'shipping';
                this.order.isBilling = this.currentStep === 'billing';
                this.order.isSummary = this.currentStep === 'summary';
                this.order.isShippingCompleted = ['billing', 'summary'].includes(this.currentStep);
                this.order.isBillingCompleted = this.currentStep === 'summary';
                this.eventBus().emit('order.updated', this.order);
            }

            if (!isPreventButtonUpdating) {
                this.eventBus().emit('checkout.step.updated', this.getNextButtonData());
            }
        }

        /**
         * @description Activates checkout step by step name
         * @param name - Name of step
         * @param historyReplace - If we need to replace history state instead of push it
         */
        activateStepByName(name: string, historyReplace = false) {
            super.activateStepByName(name, historyReplace);
            this.updateSummary();
        }

        /**
         * @description Update summary block and go to the next step
         * @param formWidget - Billing form widget
         * @param data - Submission response from server
         * @param data.order - order model
         */
        updateSummaryAndNext(formWidget: TShippingFormInstance | TBillingFormInstance, data: { order: TOrderModel }) {
            if (data.order) {
                this.order = data.order;
                this.updateSelectedPaymentMethods(data.order);
                this.nextStep();
            }
        }

        /**
         * @description Update Selected Payment Methods
         * @param order order data
        */
        updateSelectedPaymentMethods(order) {
            if (order.billing
                && order.billing.payment
                && order.billing.payment.selectedPaymentInstruments
            ) {
                order.billing.payment.selectedPaymentInstruments.forEach(pi => {
                    pi[pi.paymentMethod] = true;
                });
            }
        }
    }

    return CheckoutSummaryMixin;
}

export type TCheckoutSummaryMixin = ReturnType<typeof CheckoutSummaryMixinClassCreator>;

export type TCheckoutSummaryMixinInstance = InstanceType<TCheckoutSummaryMixin>;

export default CheckoutSummaryMixinClassCreator;
